import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Grid, Text, Title, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconCalendar } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { selectPatientDetails } from '@/store/selectors/transmissionReportPatientSelectors';
import { getIsPractitioner } from '@/store/selectors/transmissionReportSelectors';
import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { sexOptions } from '@/utils/constants';
import { formatDateWithAge } from '@/utils/formatDate.ts';
import usePatientStatusTooltip from '@/hooks/usePatientStatusTooltip';
import InfoIcon from '@/assets/icons/InfoIcon';

import PatientStatusModal from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/DetailsSection/PatientStatusModal';
import NextTransmissionSection from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/NextTransmissionSection';
import { PatientActiveStatusEnum } from '@/types/patient';
import Dropdown from '@/ui/Dropdown';
import LabeledField from '@/ui/LabeledField';

import styles from './DetailsSection.module.scss';

type EditableField = 'mrn2' | 'birthDate' | 'sex' | 'patientStatus';

interface DetailsSectionProps {
  patientId: string;
}

const useEditableField = (initialValue: string) => {
  const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const updateValue = useCallback((val: string) => setValue(val), []);
  const syncValue = useCallback(
    (external: string) => {
      if (external !== value && !isEditing && !isLoading) setValue(external);
    },
    [value, isEditing, isLoading]
  );

  return {
    value,
    isEditing,
    isLoading,
    startEditing: () => setIsEditing(true),
    stopEditing: () => setIsEditing(false),
    setLoading: setIsLoading,
    updateValue,
    syncValue
  };
};

const TooltipItems = memo(({ items }: { items: { label: string; value: string }[] }) => (
  <Text size="xs" c="white">
    {items.map(({ label, value }) => (
      <span key={label}>
        <b>{label}:</b> {value}
        <br />
      </span>
    ))}
  </Text>
));

const DetailsSection = memo(({ patientId }: DetailsSectionProps) => {
  const patient = useSelector((state: RootState) => selectPatientDetails(state, patientId));
  const isPractitioner = useAppSelector(getIsPractitioner);

  const [updatePatientDetails] = useUpdatePatientMutation();

  const mrn2 = useEditableField(patient?.mrn2 || '');
  const dob = useEditableField(patient?.birthDate || '');
  const sex = useEditableField(patient?.sex || '');
  const patientStatus = useEditableField(patient?.patientStatus || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const formData = useMemo(
    () => ({
      mrn2: mrn2.value,
      birthDate: dob.value,
      sex: sex.value
    }),
    [mrn2.value, dob.value, sex.value]
  );

  useEffect(() => {
    if (patient && !isUpdating) {
      mrn2.syncValue(patient?.mrn2 || '');
      dob.syncValue(patient?.birthDate || '');
      sex.syncValue(patient?.sex || '');
    }
  }, [patient, isUpdating]);

  const tooltipData = usePatientStatusTooltip(patient);

  const showEditIcon = useMemo(() => !isPractitioner, [isPractitioner]);

  const handleUpdate = useCallback(
    async (field: EditableField, value: string) => {
      const fieldMap = { mrn2, birthDate: dob, sex, patientStatus };
      const fieldHook = fieldMap[field];

      fieldHook.stopEditing();
      fieldHook.setLoading(true);
      setIsUpdating(true);

      try {
        await updatePatientDetails({ patient: { id: patientId, [field]: value } }).unwrap();

        fieldHook.updateValue(value);
        notifications.show({
          title: 'Success',
          message: `Patient ${field} updated successfully.`,
          color: 'green'
        });
        setTimeout(() => setIsUpdating(false), 800);
      } catch {
        notifications.show({
          title: 'Error',
          message: `Failed to update patient ${field}.`,
          color: 'red'
        });
        setIsUpdating(false);
      }
      fieldHook.setLoading(false);
    },
    [updatePatientDetails, patientId]
  );

  const renderEditableContent = useCallback(
    (field: EditableField) => {
      if (field === 'birthDate') {
        return (
          <DatePickerInput
            value={dob.value ? new Date(dob.value) : null}
            onChange={(date) => {
              if (date) {
                const formatted = dayjs(date).format('YYYY-MM-DD');
                dob.updateValue(formatted);
                handleUpdate('birthDate', formatted);
              }
            }}
            maxDate={new Date()}
            clearable={false}
            numberOfColumns={1}
            valueFormat="MM-DD-YYYY"
            rightSection={<IconCalendar size={18} stroke={1.5} />}
          />
        );
      }

      if (field === 'sex') {
        return (
          <Dropdown
            options={sexOptions}
            value={sexOptions.find((opt) => opt.value === sex.value)}
            onChange={(val) => {
              if (val && !Array.isArray(val)) {
                sex.updateValue(val.value);
                handleUpdate('sex', val.value);
              }
            }}
          />
        );
      }

      return null;
    },
    [dob.value, sex.value, handleUpdate]
  );

  const getStatusClassName = useCallback(
    (status: string) =>
      status?.toLowerCase() === 'active' ? styles.statusActive : styles.statusInactive,
    []
  );

  const toTitleCase = (str: string) => {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getPatientStatus = (status?: string) => {
    if (!status || !['Paused', 'Inactive'].includes(status)) return PatientActiveStatusEnum.Active;
    else if (status === 'Paused') return PatientActiveStatusEnum.Paused;
    return PatientActiveStatusEnum.Inactive;
  };

  const renderStatusTooltip = useCallback(() => {
    if (!tooltipData.shouldShow) {
      return (
        <div className={styles.infoIconWrapper}>
          <InfoIcon />
        </div>
      );
    }

    return (
      <Tooltip
        label={
          <Box>
            <TooltipItems items={tooltipData.items} />
          </Box>
        }
        multiline
        position="bottom-start"
        withArrow
        classNames={{ tooltip: styles.tooltip }}>
        <div className={styles.infoIconWrapper}>
          <InfoIcon />
        </div>
      </Tooltip>
    );
  }, [tooltipData]);

  if (!patient) return <div>Error loading Patient Details</div>;

  return (
    <div>
      <div className={styles.header}>
        <Title className={styles.patientName}>
          {patient.familyName} {patient.givenName}
        </Title>
        <Text className={styles.clinicName}>{patient.clinic?.name}</Text>
      </div>

      <Grid pt={'md'}>
        <Grid.Col span={{ xs: 12, sm: 4, md: 2 }}>
          <LabeledField
            label="Patient Status"
            value={
              <div className={styles.statusWrapper}>
                <Text className={styles.statusText}>
                  {toTitleCase(getPatientStatus(patient.patientStatus))}
                </Text>
                {renderStatusTooltip()}
              </div>
            }
            onEdit={() => setIsStatusModalOpen(true)}
            showEditIcon={showEditIcon}
            valueClassName={getStatusClassName(getPatientStatus(patient.patientStatus))}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 4, lg: 2 }}>
          <LabeledField label="Rhythm Id" value={patient.rhythmId} />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 4, lg: 2 }}>
          <LabeledField
            label="MRN"
            value={formData.mrn2}
            isEditable={mrn2.isEditing}
            onEdit={mrn2.startEditing}
            onChange={(e) => mrn2.updateValue(e.target.value)}
            onSubmit={(val) => handleUpdate('mrn2', val)}
            onBlur={() => handleUpdate('mrn2', mrn2.value)}
            isLoading={mrn2.isLoading}
            showEditIcon={showEditIcon}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 4, lg: 3 }}>
          <LabeledField
            label="DOB"
            value={formatDateWithAge(formData.birthDate, 'MM/DD/YYYY')}
            isEditable={dob.isEditing}
            onEdit={dob.startEditing}
            onSubmit={(val) => handleUpdate('birthDate', val)}
            customEditableComponent={renderEditableContent('birthDate')}
            isLoading={dob.isLoading}
            showEditIcon={showEditIcon}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 4, lg: 2 }}>
          <LabeledField
            label="Sex"
            value={formData.sex?.toUpperCase()}
            isEditable={sex.isEditing}
            onEdit={sex.startEditing}
            onSubmit={(val) => handleUpdate('sex', val)}
            customEditableComponent={renderEditableContent('sex')}
            isLoading={sex.isLoading}
            showEditIcon={showEditIcon}
          />
        </Grid.Col>
      </Grid>

      <NextTransmissionSection patientId={patientId} />

      <PatientStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        patientId={patientId}
        currentStatus={patient.patientStatus}
        currentReason={patient.statusReason}
        currentStartDate={patient.statusStartDate}
        currentEndDate={patient.statusEndDate}
        statusOtherReason={patient.statusOtherReason}
      />
    </div>
  );
});

DetailsSection.displayName = 'DetailsSection';

export default DetailsSection;
